import { Heart } from "lucide-react";
import { toast } from "sonner";
import { useRouter } from "@tanstack/react-router";
import { addToWishlist, makeKey, removeFromWishlist, useInWishlist } from "@/lib/wishlist";
import { useIsSignedIn } from "@/lib/auth-context";
import { normalizeTcgLabel } from "@/lib/marketplace";

interface Props {
  cardName: string;
  tcg: string;
  set?: string;
  cardNumber?: string;
  className?: string;
  size?: "sm" | "md";
}

export function WishlistButton({ cardName, tcg: tcgRaw, set, cardNumber, className = "", size = "md" }: Props) {
  const router = useRouter();
  const signedIn = useIsSignedIn();
  const tcg = normalizeTcgLabel(tcgRaw);
  const key = makeKey({ cardName, tcg, set, cardNumber });
  const active = useInWishlist(key);
  const dim = size === "sm" ? "h-7 w-7" : "h-9 w-9";
  const icon = size === "sm" ? "h-3.5 w-3.5" : "h-4 w-4";

  function onClick(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (!signedIn) {
      toast.message("Sign in to use your Watchlist", {
        description: "Save buy orders you want to keep an eye on and jump back in any time.",
        action: { label: "Sign in", onClick: () => router.navigate({ to: "/auth" }) },
      });
      return;
    }
    if (active) {
      removeFromWishlist(key);
      toast("Removed from Watchlist");
    } else {
      addToWishlist({ cardName, tcg, set, cardNumber });
      toast.success(`Added "${cardName}" to Watchlist`);
    }
  }

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={active ? "Remove from watchlist" : "Add to watchlist"}
      title={active ? "Remove from watchlist" : "Add to watchlist"}
      className={`grid ${dim} place-items-center rounded-full border border-border bg-background/80 backdrop-blur transition hover:border-primary hover:text-primary ${
        active ? "text-primary" : "text-muted-foreground"
      } ${className}`}
    >
      <Heart className={`${icon} ${active ? "fill-primary" : ""}`} />
    </button>
  );
}
